import dynamic from 'next/dynamic';
import { useContext } from 'react';
import FeatureBranch from '@/common/FeatureBranch';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import BreadCrumbs from '@/vdp/BreadCrumbs';
import DeliveryUnavailableBanner from '@/vdp/DeliveryUnavailableBanner';
import { HeroContextProvider } from '@/vdp/Hero/HeroContext';
import HeroLite from '@/vdp/HeroLite';
import DeliveryAndPickupLite from '@/vdp/LiteTextBoxes';
import PriceDetails from '@/vdp/PriceDetails';
import FeaturesAndHighlights from '~/components/vdp/FeaturesAndHighlights';
import Sections from '~/constants/vdp/sections';
import { useIsDesktop } from '~/context/common/DeviceContext';
import ModalContext from '~/context/vdp/ModalContext';
import useCtaButtonState from '~/hooks/vdp/selectors/useCtaButtonState';
import useShouldShowStatusDataForExperiment from '~/hooks/vdp/useShouldShowStatusDataForExperiment';
import SectionsWrapper from './SectionsWrapper';
import ShowConditionally from './ShowConditionally';
import { Wrapper } from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice')
);

const AncillaryProducts = dynamic(
  () =>
    import(
      /* webpackChunkName: "AncillaryProducts" */ '@/vdp/AncillaryProducts'
    ),
  { ssr: false }
);

const CreditKarmaPromoBanner = dynamic(
  () =>
    import(
      /* webpackChunkName: "CreditKarmaPromoBanner" */ '@/common/CreditKarmaPromoBanner'
    ),
  { ssr: false }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  { ssr: false }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const MoneyBackGuarantee = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoneyBackGuarantee" */ '@/vdp/MoneyBackGuarantee'
    ),
  { ssr: false }
);

const OnHoldBanner = dynamic(
  () => import(/* webpackChunkName: "OnHoldBanner" */ '@/vdp/OnHoldBanner'),
  { ssr: false }
);

const One50PointInspection = dynamic(
  () => import(/* webpackChunkName: "150PointInspection" */ '@/vdp/Inspection'),
  { ssr: false }
);

const OwnerReviews = dynamic(
  () => import(/* webpackChunkName: "OwnerReviews" */ '@/vdp/OwnerReviews'),
  { ssr: false }
);

const RecommendedVehicles = dynamic(
  () =>
    import(
      /* webpackChunkName: "RecommendedVehicles" */ '@/vdp/RecommendedVehicles'
    ),
  {
    ssr: false
  }
);

const StatusBanner = dynamic(
  () => import(/* webpackChunkName: "StatusBanner" */ '@/vdp/StatusBanner'),
  { ssr: false }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const StickyCtaLite = dynamic(
  () => import(/* webpackChunkName: "StickyCtaLite" */ '@/vdp/StickyCtaLite')
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  { ssr: false }
);

const VehicleDetailsLiteContainer = () => {
  const { notifyMe } = useCtaButtonState();
  const shouldShowStatusData = useShouldShowStatusDataForExperiment();
  const isDesktop = useIsDesktop();
  const { setCreditKarmaPromoModalOpen } = useContext(ModalContext);

  const showStickyCtaLite = !isDesktop && (!notifyMe || shouldShowStatusData);

  return (
    <>
      <FeatureBranch />
      <PageHeader />
      <Wrapper id={Sections.Root}>
        <SectionsWrapper>
          <CreditKarmaPromoBanner
            setCreditKarmaPromoModalOpen={setCreditKarmaPromoModalOpen}
          />
          <StickyBanner />
          <BreadCrumbs />
          <OnHoldBanner />
          <DeliveryUnavailableBanner />
          <StatusBanner />
          <HeroContextProvider>
            <HeroLite />
          </HeroContextProvider>
          <ShowConditionally notAvailableForImmediatePurchase>
            <div id={Sections.RecommendedVehicles}>
              <RecommendedVehicles />
            </div>
          </ShowConditionally>
          <DeliveryAndPickupLite />
          <ShowConditionally aboutPrice>
            <AboutThePrice />
          </ShowConditionally>
          <ShowConditionally notAvailableForImmediatePurchase={false}>
            <div id={Sections.RecommendedVehicles}>
              <RecommendedVehicles />
            </div>
          </ShowConditionally>
          <div id={Sections.PriceDetails}>
            <PriceDetails whiteBg />
          </div>
          <FeaturesAndHighlights />
          <div id={Sections.VehicleDetails}>
            <VehicleDetails />
          </div>
          <div id={Sections.Inspection}>
            <One50PointInspection />
          </div>
          <div id={Sections.MoneyBackGuarantee}>
            <MoneyBackGuarantee />
          </div>
          <div id={Sections.AncillaryProducts}>
            <AncillaryProducts />
          </div>
          <ShowConditionally ownerReviews>
            <div id={Sections.OwnerReviews}>
              <OwnerReviews />
            </div>
          </ShowConditionally>
        </SectionsWrapper>
        {showStickyCtaLite && <StickyCtaLite />}
        <Disclaimer />
      </Wrapper>
      <PageFooter />
      <Modals />
    </>
  );
};

export default VehicleDetailsLiteContainer;
